import { createFileRoute } from '@tanstack/react-router';
import { lazy, Suspense } from 'react';
import { z } from 'zod';

import { PageSkeleton } from '~/components/common/page-skeleton/page-skeleton';
import { QUALITY_VIEWS } from '~/features/content/utils/quality-labels';
import { RUN_TAB_PARAMS, RUN_TECH_VIEWS, resolveRunTab } from '~/features/content/utils/run-tabs';

const ContentRunDetailPage = lazy(() => import('~/features/content/pages/content-run-detail-page'));

// The active tab lives in the URL so a reload, a shared link or the back button
// lands on the same section. `RUN_TAB_PARAMS` holds every tab (incl. a scoring job's
// overview/products/rules/fields) plus legacy names (results, failures…), which are
// mapped; a tab the job doesn't show goes to the closest it does (see `pickRunTab`).
const searchSchema = z.object({
  // Optional so existing links to a job need not name a tab.
  tab: z.enum(RUN_TAB_PARAMS).optional().catch(undefined),
  /** Sub-view inside Technical details. */
  tech: z.enum(RUN_TECH_VIEWS).optional().catch(undefined),
  /** Channel shown on the Quality tab, for a job scored on several channels. */
  qplatform: z.string().optional().catch(undefined),
  /** Quality tab "View as": every rule together, or one business view (SEO / AEO / GEO). */
  qview: z.enum(QUALITY_VIEWS).optional().catch(undefined),
  /** Quality tab batch (0-based); absent = every batch. */
  qbatch: z.number().int().nonnegative().optional().catch(undefined),
});

/** One job (generation run) at `/content/runs/$runId`, with its job-scoped reads as tabs. */
export const Route = createFileRoute('/_authenticated/_tenant/content/runs/$runId')({
  validateSearch: searchSchema,
  component: RunDetailRoute,
});

function RunDetailRoute() {
  const { runId } = Route.useParams();
  const search = Route.useSearch();
  const { tab, tech } = resolveRunTab(search.tab, search.tech);
  return (
    <Suspense fallback={<PageSkeleton />}>
      <ContentRunDetailPage
        runId={runId}
        tab={tab}
        tech={tech}
        qualityPlatform={search.qplatform}
        qualityView={search.qview}
        qualityBatch={search.qbatch}
      />
    </Suspense>
  );
}
